import { Helmet } from "react-helmet-async";
import { useLanguage, Language } from "@/i18n/LanguageContext";

const SITE_URL = "https://banking.4tagewoche.org";

const OG_LOCALES: Record<Language, string> = {
  de: "de_DE",
  fr: "fr_FR",
  en: "en_US",
};

const ALTERNATES: Language[] = ["de", "fr", "en"];

function buildUrl(path: string, lang: Language) {
  const base = `${SITE_URL}${path}`;
  if (lang === "de") return base;
  return `${base}${path.includes("?") ? "&" : "?"}lang=${lang}`;
}

interface SEOProps {
  title: string;
  description: string;
  path: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export function SEO({ title, description, path, jsonLd, noindex }: SEOProps) {
  const { language } = useLanguage();
  const canonical = buildUrl(path, language);

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {ALTERNATES.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={buildUrl(path, lang)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={buildUrl(path, "de")} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={OG_LOCALES[language]} />
      {ALTERNATES.filter((lang) => lang !== language).map((lang) => (
        <meta key={lang} property="og:locale:alternate" content={OG_LOCALES[lang]} />
      ))}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
